import { type InputHTMLAttributes, useId } from 'react';

import { Icon, type IconName } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';

import { Field } from './Field';
import styles from './Field.module.css';
import { fieldDescriptionIds } from './field-ids';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** Icon inside the input, on the left. */
  icon?: IconName;
  /** Short text inside the input, on the left — e.g. "₹". */
  prefix?: string;
  fieldClassName?: string | undefined;
}

/** Input types that carry a browser picker (a calendar, a clock). */
const PICKER_TYPES = new Set(['date', 'time', 'datetime-local', 'month', 'week']);

/**
 * Where a click landed inside a date field. The calendar opens only from the icon on the right;
 * clicking the digits puts the caret there instead, so a date can be typed — a birth date decades
 * back is far quicker to type than to scroll a calendar to.
 */
const INDICATOR_WIDTH_PX = 36;

function clickedThePickerIcon(input: HTMLInputElement, clientX: number): boolean {
  const { right } = input.getBoundingClientRect();
  return clientX >= right - INDICATOR_WIDTH_PX;
}

function openPicker(input: HTMLInputElement) {
  if (input.readOnly || input.disabled) return;
  try {
    input.showPicker();
  } catch {
    // Older browsers lack showPicker, and it refuses outside a user gesture; the field still
    // works by typing or through its own icon.
  }
}

export function TextField({
  label,
  hint,
  error,
  icon,
  prefix,
  id,
  required,
  className,
  fieldClassName,
  ...inputProps
}: TextFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hasPicker = PICKER_TYPES.has(inputProps.type ?? 'text');
  const { onClick } = inputProps;

  return (
    <Field
      label={label}
      htmlFor={inputId}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
    >
      <div className={styles.control}>
        {icon && <Icon name={icon} size={18} className={styles.leading} />}
        {prefix && !icon && <span className={styles.prefix}>{prefix}</span>}
        <input
          id={inputId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={fieldDescriptionIds(inputId, hint, error)}
          className={cn(
            styles.input,
            (icon ?? prefix) !== undefined && styles.withLeading,
            error && styles.invalid,
            hasPicker && styles.picker,
            className,
          )}
          {...inputProps}
          onClick={(event) => {
            // The icon opens the calendar; the rest of the field is left alone so the date can
            // also be typed. Both ways work on every date field.
            if (hasPicker && clickedThePickerIcon(event.currentTarget, event.clientX)) {
              openPicker(event.currentTarget);
            }
            onClick?.(event);
          }}
        />
      </div>
    </Field>
  );
}
